import {
  Button,
  Modal,
  SkeletonText,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  Tile,
  FlexGrid as Grid,
  Column,
  Row,
  TableCell,
  InlineNotification,
} from '@carbon/react';
import { DestroyConfirmation } from 'components/destroy-confirmation';
import { Section } from 'components/section';
import { StatusIndicator } from 'components/status-indicator';
import { PaginatedTable, RemovableRow } from 'components/table';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { usePagination } from 'lib/hooks/usePagination';
import { useReducer, useState, VFC, ReactNode } from 'react';
import {
  useInterpretationsAtom,
  documentProvisionStore,
  refreshDocumentCounter,
} from '../common/atoms';
import type { DocumentPageProps } from '../common/header';
import {
  removedAreasAtom,
  removedPartiesAtom,
  runMutationAtom,
  useAreasAtom,
  usePartiesAtom,
  useReferencedDocumentsAtom,
  useTransactionPartiesAtom,
} from './atoms';
import {
  ERROR_TEXT,
  extractErrorMessage,
  markAsDuplicate,
  resetRowSelectionAndError,
  toSentence,
} from './utils';
import style from './page.module.scss';
import { Buttons } from 'components/buttons';
import { CopyFile } from '@carbon/react/icons';
import { Link } from 'components/link';
import {
  DocumentInterpretationResponse,
  DocumentResponse,
  TransactionPartyResponse,
} from 'types/api-responses';
import { Helmet } from 'react-helmet-async';
import { pageTitle } from 'utils/page-title';
import * as ProvisionedDocument from 'components/provisioned-document';
import { capitalizeFirstLetter } from 'utils/strings';
import { documentLayoutAtom } from 'pages/documents/structuring/atoms/document-layout';
import type { FeatureFlagWindow } from 'types/window';
import { Default } from 'components/icons';
import { EmptyState } from 'components/empty-state';
import { TitleChainValidator } from 'components/title-chain-validator';
import { StructuringModule } from '../structuring/structuring-module';
import {
  AfeCostInformationSection,
  AfeWellInformationSection,
} from './afe-tables';
import {
  DivisionOrderWellsCitations,
  PartyCitations,
  RegulatedTargetCitations,
  RegulatoryBodyCitations,
} from './citation-tables';
import { DivisionOrderLinesTable } from './division-order-lines-table';
import { OverviewMapSection } from './overview-map-section';
import {
  RoyaltyCheckLineItemsTable,
  RoyaltyCheckWellsCitations,
} from './royalty-check-tables';

declare let window: FeatureFlagWindow;

const PartiesInvolvedSection = ({
  interpretations,
  document,
  isLoading,
  transactionParties,
}: {
  interpretations: DocumentInterpretationResponse[] | undefined;
  document: DocumentResponse;
  isLoading: boolean;
  transactionParties: {
    data: TransactionPartyResponse[] | { error: string } | undefined;
    loading: boolean;
  };
}) => {
  return (
    <Section header="Parties Involved">
      <PartiesInvolved
        interpretations={interpretations}
        document={document}
        isLoading={isLoading}
        transactionParties={transactionParties}
      />
    </Section>
  );
};

const ReferencedDocumentsSection = ({
  interpretations,
  document,
  isLoading,
  getDocument,
}: {
  interpretations: DocumentInterpretationResponse[] | undefined;
  document: DocumentResponse;
  isLoading: boolean;
  getDocument: () => Promise<void>;
}) => {
  return (
    <Section header="Referenced Documents">
      <ReferencedDocuments
        interpretations={interpretations}
        document={document}
        isLoading={isLoading}
      />
      <MarkAsDuplicate document={document} getDocument={getDocument} />
    </Section>
  );
};

const PageContentV2 = ({
  document,
  getDocument,
}: {
  document: DocumentResponse;
  getDocument: () => Promise<void>;
}) => {
  const refreshDocument = useSetAtom(refreshDocumentCounter);
  const interpretationsAtom = useInterpretationsAtom(
    document.resources.interpretations.href
  );
  const interpretations = useAtomValue(interpretationsAtom);

  const transactionPartiesAtom = useTransactionPartiesAtom(
    document.resources.transactionParties.href
  );
  const transactionParties = useAtomValue(transactionPartiesAtom);

  const { showMapAndDetails } = useAtomValue(documentLayoutAtom);

  if (interpretations.data && 'error' in interpretations.data) {
    return (
      <InlineNotification
        kind="error"
        title={interpretations.data.error}
        lowContrast
      />
    );
  }
  const isCurrentDocumentPrimary =
    document.primarySiblingDocument?.id === document.id;

  const hasInterpretations =
    interpretations.data &&
    !('error' in interpretations.data) &&
    interpretations.data.length > 0;

  const hasTransactionParties =
    transactionParties.data &&
    !('error' in transactionParties.data) &&
    transactionParties.data.length > 0;

  const isStructuringEnabled =
    !!window.CONFIG?.featureFlags.documentsEnableTitleChainValidatorTab;
  const isTitleChainValidatorEnabled =
    isStructuringEnabled && hasInterpretations && hasTransactionParties;

  return (
    <>
      {showMapAndDetails && <OverviewMapSection document={document} />}
      <SiblingDocuments document={document} />
      {isCurrentDocumentPrimary && (
        <>
          <Section header="">
            {isStructuringEnabled ? (
              <div className={`${style.tabs} ${style.tabsContained}`}>
                <Tabs>
                  <TabList contained>
                    <Tab>Structuring</Tab>
                    <Tab disabled={!isTitleChainValidatorEnabled}>
                      Validate Title Chain
                    </Tab>
                    <Tab>Legacy</Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel>
                      <Tile className={style.tileTab}>
                        <StructuringModule
                          document={document}
                          legalDescriptionTitle=""
                          provisionsSlot={
                            <ProvisionedDocument.List
                              store={documentProvisionStore}
                              onProvisionsAvailable={() => {
                                refreshDocument();
                              }}
                              document={document}
                            />
                          }
                          legalDescriptionSlot={
                            <>
                              <AreasInvolved
                                interpretations={interpretations.data}
                                isLoading={interpretations.loading}
                                document={document}
                              />
                              {!document.structuredPartiesSupported && (
                                <PartyCitations document={document} />
                              )}
                              <RegulatoryBodyCitations document={document} />
                              <RegulatedTargetCitations document={document} />
                              <AfeWellInformationSection document={document} />
                              <AfeCostInformationSection document={document} />
                              <DivisionOrderWellsCitations
                                document={document}
                              />
                              <DivisionOrderLinesTable document={document} />
                              <RoyaltyCheckWellsCitations document={document} />
                              <RoyaltyCheckLineItemsTable document={document} />
                            </>
                          }
                        />
                      </Tile>
                    </TabPanel>
                    <TabPanel>
                      <Tile className={style.tileTab}>
                        <TitleChainValidator
                          titleValidatorUrl={
                            document.resources.validateTitleChain.href
                          }
                          documentId={document.id}
                          entities={transactionParties.data || []}
                          entitiesLoading={transactionParties.loading}
                        />
                      </Tile>
                    </TabPanel>
                    <TabPanel>
                      <Tile className={style.tileTab}>
                        <PartiesInvolvedSection
                          interpretations={interpretations.data}
                          document={document}
                          isLoading={interpretations.loading}
                          transactionParties={transactionParties}
                        />
                        <ReferencedDocumentsSection
                          interpretations={interpretations.data}
                          document={document}
                          isLoading={interpretations.loading}
                          getDocument={getDocument}
                        />
                      </Tile>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </div>
            ) : (
              <Grid fullWidth condensed className="bx--no-gutter">
                <Row className={style.mapRow}>
                  <Column sm={6} lg={6} className={style.detail}>
                    <ProvisionedDocument.List
                      store={documentProvisionStore}
                      onProvisionsAvailable={() => {
                        refreshDocument();
                      }}
                      document={document}
                    />
                  </Column>
                  <Column sm={10} lg={10} className={style.detail}>
                    <AreasInvolved
                      interpretations={interpretations.data}
                      isLoading={interpretations.loading}
                      document={document}
                    />
                    {!document.structuredPartiesSupported && (
                      <PartyCitations document={document} />
                    )}
                    <RegulatoryBodyCitations document={document} />
                    <RegulatedTargetCitations document={document} />
                    <AfeWellInformationSection document={document} />
                    <AfeCostInformationSection document={document} />
                    <DivisionOrderWellsCitations document={document} />
                    <DivisionOrderLinesTable document={document} />
                    <RoyaltyCheckWellsCitations document={document} />
                    <RoyaltyCheckLineItemsTable document={document} />
                  </Column>
                </Row>
              </Grid>
            )}
          </Section>
          {!isStructuringEnabled && (
            <>
              <PartiesInvolvedSection
                interpretations={interpretations.data}
                document={document}
                isLoading={interpretations.loading}
                transactionParties={transactionParties}
              />
              <ReferencedDocumentsSection
                interpretations={interpretations.data}
                document={document}
                isLoading={interpretations.loading}
                getDocument={getDocument}
              />
            </>
          )}
        </>
      )}
    </>
  );
};

const SiblingDocuments = ({ document }: { document: DocumentResponse }) => {
  const siblings = document.siblingDocuments || [];
  const primarySibling = document.primarySiblingDocument;
  const isCurrentDocumentPrimary = primarySibling.id === document.id;

  if (siblings.length === 0) {
    return null;
  }

  if (!isCurrentDocumentPrimary) {
    return (
      <Section header="Sibling Documents">
        <p>
          <Link
            to={`/documents/${primarySibling.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            View Primary Sibling Document: {primarySibling.id}
          </Link>
        </p>
      </Section>
    );
  }

  return (
    <Section header="Sibling Documents">
      {siblings.map((sibling) => (
        <p key={sibling.id}>
          <Link to={`/documents/${sibling.id}`}>
            View Sibling Document: {sibling.id}
          </Link>
        </p>
      ))}
    </Section>
  );
};

const AreasInvolved = ({
  interpretations,
  isLoading,
  document,
}: {
  interpretations: DocumentInterpretationResponse[] | undefined;
  isLoading: boolean;
  document: DocumentResponse;
}) => {
  const [selectedRowIndex, setSelectedRowIndex] = useState(-1);
  const [error, setError] = useState<string | undefined>(undefined);
  const [mutation, runMutation] = useAtom(runMutationAtom);
  const areasAtom = useAreasAtom(interpretations, document.locations);
  const areas = useAtomValue(areasAtom);
  const setRemovedParties = useSetAtom(removedAreasAtom);

  // Get all citation IDs that are associated with locations
  const locationCitationIds = new Set(
    document.locations?.flatMap((location) =>
      location.citations?.map((citation) => citation.id)
    ) || []
  );

  const legalDescriptionProvisionField =
    document.type === 'Document::RoyaltyCheck'
      ? 'legal_descriptions.full_legal_description'
      : 'Legal Description Component';

  const unassociatedCitations =
    document.citations?.filter(
      (citation) =>
        !locationCitationIds.has(citation.id) &&
        citation.provisionField === legalDescriptionProvisionField
    ) || [];

  // Combine areas and citations for display
  const allItems = [
    ...areas,
    ...unassociatedCitations.map((citation) => ({
      id: citation.id,
      legalDescriptionComponent: citation.provisionField,
      name: '',
      quarterCalls: '',
      interpretationIndices: [],
      provisionValue: citation.provisionValue,
      href: '',
    })),
  ];

  const pageSize = 10;
  const { page, slicedItems, onPaginate } = usePagination<(typeof allItems)[0]>(
    allItems,
    pageSize
  );

  const item = allItems[selectedRowIndex];

  return (
    <>
      <DestroyConfirmation
        error={error}
        open={selectedRowIndex >= 0}
        primaryButtonDisabled={mutation.loading}
        onRequestClose={() => {
          resetRowSelectionAndError(setSelectedRowIndex, setError);
        }}
        onRequestSubmit={() => {
          runMutation({
            url: item.href,
            type: 'DELETE',
            data: null,
            onSuccess: () => {
              setRemovedParties((current) => [...current, item.id]);
              resetRowSelectionAndError(setSelectedRowIndex, setError);
            },
            onError: (response) => {
              const errorMessage = response
                ? extractErrorMessage(response)
                : ERROR_TEXT;
              setError(errorMessage);
            },
          });
        }}
      />
      <Section header="Full Legal Description">
        <PaginatedTable
          pageSizes={[10, 20, 30]}
          pageSize={pageSize}
          page={page}
          totalItems={allItems.length}
          onPaginate={onPaginate}
          isLoading={isLoading}
          emptyStateComponent={
            <Tile className={style.tileAdjustedForTableContainer}>
              <EmptyState
                headerText="No data"
                helperText="We couldn't find any available data."
                IconComponent={Default}
              />
            </Tile>
          }
          data-testid="applicable-areas-table"
        >
          <TableHead>
            <TableRow>
              <TableHeader>Legal Description Component</TableHeader>
              <TableHeader>Applicable Areas</TableHeader>
              <TableHeader>Quarter Calls</TableHeader>
              <TableHeader colSpan={2}>Status</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {slicedItems.map((el, i) => {
              const isCitation = 'provisionValue' in el;
              return (
                <RemovableRow
                  onRemove={(index) => {
                    setSelectedRowIndex(index);
                  }}
                  markedForDestruction={i === selectedRowIndex}
                  index={i}
                  key={el.id}
                  removable={
                    !isCitation && el.interpretationIndices?.length === 0
                  }
                >
                  <TableCell>
                    {isCitation ? (
                      <>
                        <small>{el.provisionValue as ReactNode}</small>
                      </>
                    ) : (
                      el.legalDescriptionComponent
                    )}
                  </TableCell>
                  <TableCell>{el.name}</TableCell>
                  <TableCell>{el.quarterCalls}</TableCell>
                  <TableCell>
                    {!isCitation && !el.interpretationIndices ? (
                      <SkeletonText />
                    ) : (
                      <Status
                        interpretationIndices={el.interpretationIndices || []}
                      />
                    )}
                  </TableCell>
                </RemovableRow>
              );
            })}
          </TableBody>
        </PaginatedTable>
      </Section>
    </>
  );
};

const PartiesInvolved = ({
  interpretations,
  document,
  isLoading,
  transactionParties,
}: {
  interpretations: DocumentInterpretationResponse[] | undefined;
  document: DocumentResponse;
  isLoading: boolean;
  transactionParties: {
    data: TransactionPartyResponse[] | { error: string } | undefined;
    loading: boolean;
  };
}) => {
  return (
    <div className={`${style.tabs} ${style.tabsContained}`}>
      <Tabs>
        <TabList contained>
          <Tab>Text based</Tab>
          <Tab>Entity based</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <Tile className={style.tileTab}>
              <TextBasedParties
                interpretations={interpretations}
                document={document}
                isLoading={isLoading}
              />
            </Tile>
          </TabPanel>
          <TabPanel>
            <Tile className={style.tileTab}>
              {transactionParties.data && 'error' in transactionParties.data ? (
                <InlineNotification
                  kind="error"
                  title={transactionParties.data.error}
                  lowContrast
                />
              ) : (
                <EntityBasedParties
                  parties={transactionParties.data || []}
                  isLoading={transactionParties.loading}
                />
              )}
            </Tile>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  );
};

const EntityBasedParties = ({
  parties,
  isLoading,
}: {
  parties: TransactionPartyResponse[];
  isLoading: boolean;
}) => {
  const pageSize = 10;
  const { page, slicedItems, onPaginate } =
    usePagination<TransactionPartyResponse>(parties, pageSize);

  if (!parties.length) {
    return <p>There are no entity based parties.</p>;
  }

  return (
    <PaginatedTable
      pageSizes={[10, 20, 30]}
      pageSize={pageSize}
      page={page}
      totalItems={parties.length}
      onPaginate={onPaginate}
      isLoading={isLoading}
      emptyStateComponent={
        <Tile className={style.tileAdjustedForTableContainer}>
          <EmptyState
            headerText="No entity based parties"
            helperText="There are no entity based parties available."
            IconComponent={Default}
          />
        </Tile>
      }
      data-testid="entity-based-table"
    >
      <TableHead>
        <TableRow>
          <TableHeader>Applicable Parties</TableHeader>
          <TableHeader>ID</TableHeader>
          <TableHeader>Capacity</TableHeader>
          <TableHeader colSpan={2}>Status</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {slicedItems.map((el) => {
          return (
            <TableRow key={el.entity.id}>
              <TableCell>
                <Link to={el.entity.href}>{el.entity.name}</Link>
              </TableCell>
              <TableCell>{el.entity.id}</TableCell>
              <TableCell>{capitalizeFirstLetter(el.role)}</TableCell>
              <TableCell>
                <Status interpretationIndices={el.interpretationIndices} />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </PaginatedTable>
  );
};

const TextBasedParties = ({
  interpretations,
  document,
  isLoading,
}: {
  interpretations: DocumentInterpretationResponse[] | undefined;
  document: DocumentResponse;
  isLoading: boolean;
}) => {
  const [selectedRowIndex, setSelectedRowIndex] = useState(-1);
  const [error, setError] = useState<string | undefined>(undefined);
  const [mutation, runMutation] = useAtom(runMutationAtom);
  const partiesAtom = usePartiesAtom(
    interpretations,
    document.grantors,
    document.grantees
  );
  const parties = useAtomValue(partiesAtom);
  const setRemovedParties = useSetAtom(removedPartiesAtom);
  const pageSize = 10;
  const { page, slicedItems, onPaginate } = usePagination<(typeof parties)[0]>(
    parties,
    pageSize
  );

  const party = parties[selectedRowIndex];

  return (
    <>
      <DestroyConfirmation
        error={error}
        open={selectedRowIndex >= 0}
        primaryButtonDisabled={mutation.loading}
        onRequestClose={() => {
          resetRowSelectionAndError(setSelectedRowIndex, setError);
        }}
        onRequestSubmit={() => {
          runMutation({
            url: party.href,
            type: 'DELETE',
            data: null,
            onSuccess: () => {
              setRemovedParties((current) => [...current, party.entityId]);
              resetRowSelectionAndError(setSelectedRowIndex, setError);
            },
            onError: (response) => {
              const errorMessage = response
                ? extractErrorMessage(response)
                : ERROR_TEXT;
              setError(errorMessage);
            },
          });
        }}
      />
      <PaginatedTable
        pageSizes={[10, 20, 30]}
        pageSize={pageSize}
        page={page}
        totalItems={parties.length}
        onPaginate={onPaginate}
        isLoading={isLoading}
        emptyStateComponent={
          <Tile className={style.tileAdjustedForTableContainer}>
            <EmptyState
              headerText="No text based parties"
              helperText="There are no text based parties available."
              IconComponent={Default}
            />
          </Tile>
        }
        data-testid="text-based-table"
      >
        <TableHead>
          <TableRow>
            <TableHeader>Applicable Parties</TableHeader>
            <TableHeader>Capacity</TableHeader>
            <TableHeader colSpan={2}>Status</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {slicedItems.map((el, i) => {
            return (
              <RemovableRow
                onRemove={(index) => {
                  setSelectedRowIndex(index);
                }}
                markedForDestruction={i === selectedRowIndex}
                index={i}
                key={el.entityId}
                removable={el.interpretationIndices?.length === 0}
              >
                <TableCell>{el.name}</TableCell>
                <TableCell>{el.type}</TableCell>
                <TableCell>
                  {!el.interpretationIndices ? (
                    <SkeletonText />
                  ) : (
                    <Status interpretationIndices={el.interpretationIndices} />
                  )}
                </TableCell>
              </RemovableRow>
            );
          })}
        </TableBody>
      </PaginatedTable>
    </>
  );
};

const ReferencedDocuments = ({
  interpretations,
  document,
  isLoading,
}: {
  interpretations: DocumentInterpretationResponse[] | undefined;
  document: DocumentResponse;
  isLoading: boolean;
}) => {
  const referencedDocumentsAtom = useReferencedDocumentsAtom(
    interpretations,
    document.referencedDocuments
  );
  const referencedDocuments = useAtomValue(referencedDocumentsAtom);
  const pageSize = 10;
  const { page, slicedItems, onPaginate } = usePagination<
    (typeof referencedDocuments)[0]
  >(referencedDocuments, pageSize);

  return (
    <PaginatedTable
      pageSizes={[10, 20, 30]}
      pageSize={pageSize}
      page={page}
      totalItems={referencedDocuments.length}
      onPaginate={onPaginate}
      isLoading={isLoading}
      emptyStateComponent={
        <Tile className={style.tileAdjustedForTableContainer}>
          <EmptyState
            headerText="No referenced documents"
            helperText="There are no referenced documents available."
            IconComponent={Default}
          />
        </Tile>
      }
      data-testid="referenced-docs-table"
    >
      <TableHead>
        <TableRow>
          <TableHeader>Document Identifier</TableHeader>
          <TableHeader>Book Type</TableHeader>
          <TableHeader>Document Type</TableHeader>
          <TableHeader colSpan={2}>Status</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {slicedItems.map((el) => {
          return (
            <TableRow key={el.id}>
              <TableCell>{el.title}</TableCell>
              <TableCell>{el.bookType}</TableCell>
              <TableCell>{el.documentType}</TableCell>
              <TableCell>
                {!el.interpretationIndices ? (
                  <SkeletonText />
                ) : (
                  <Status interpretationIndices={el.interpretationIndices} />
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </PaginatedTable>
  );
};

const Status: VFC<{ interpretationIndices: number[] }> = ({
  interpretationIndices,
}) => {
  const state = interpretationIndices.length ? 'confirmed' : 'notConfirmed';

  return (
    <StatusIndicator state={state}>
      {state === 'confirmed'
        ? `Interpretation ${toSentence(interpretationIndices)}`
        : 'Not Confirmed'}
    </StatusIndicator>
  );
};

const MarkAsDuplicate = ({
  document,
  getDocument,
}: {
  document: DocumentResponse;
  getDocument: () => Promise<void>;
}) => {
  const [open, toggleOpen] = useReducer((s) => !s, false);

  if (document && document.duplicate) {
    return null;
  }

  return (
    <>
      <Buttons justify="right">
        <Button
          renderIcon={(props) => <CopyFile size={32} {...props} />}
          kind="danger--ghost"
          onClick={toggleOpen}
        >
          Mark As Duplicate Document
        </Button>
      </Buttons>
      <Modal
        open={open}
        modalHeading="Mark as Duplicate Document"
        danger
        primaryButtonText="Yes, Mark Document"
        secondaryButtonText="Cancel"
        onRequestSubmit={async () => {
          if (document) {
            await markAsDuplicate(document.href);
            getDocument();
            toggleOpen();
          }
        }}
        onRequestClose={toggleOpen}
      >
        Are you sure you want to mark as a duplicate? This action cannot be
        undone and all of the document data will be removed from Insights.
      </Modal>
    </>
  );
};

// Content only — the document fetch and DocumentHeader live in
// DocumentEntrypointPage so they survive a tab switch.
const DocumentOverviewPage = ({ document, getDocument }: DocumentPageProps) => (
  <>
    <Helmet>
      <title>{pageTitle(`${document.documentTitle}`)}</title>
    </Helmet>
    <PageContentV2 document={document} getDocument={getDocument} />
  </>
);

export { DocumentOverviewPage };
